import { z } from 'zod';
import {
  subscriptionPlans,
  type DeletionNoticeStage,
  type MemberRole,
  type PlanFeature,
  type SubscriptionPlan,
  type SubscriptionStatus,
} from '../enums.js';
import { paginationFields, type PaginatedDto } from './_pagination.js';
import { sortDirections } from './patients.js';

/**
 * Console de plateforme — ce qui se lit à travers les cabinets.
 *
 * **Rien ici ne décrit un patient.** Pas un nom, pas une date de naissance, pas un matricule : des
 * nombres, des horodatages, et les métadonnées du cabinet. Le seul endroit où des personnes sont
 * nommées est la liste des membres d'un cabinet — c'est son personnel, celui qu'on appelle quand
 * une intégration tombe, et jamais sa patientèle.
 *
 * Ouvert à `support` et `super_admin`. Ces contrats ne portent aucun geste ; ils décrivent
 * uniquement ce qu'on regarde.
 */

/* -------------------------------------------------------------------------- */
/* Vue d'ensemble                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Les chiffres de la première page. **Des nombres, jamais des montants** : la console surveille un
 * service, elle ne fait pas la comptabilité des cabinets, et le chiffre d'affaires d'un praticien
 * ne nous regarde pas.
 */
export interface PlatformStatsDto {
  organizations: number;
  /** Comptes actifs, tous cabinets confondus. Un compte désactivé ne compte pas. */
  activeUsers: number;
  /** Dossiers vivants : ni en corbeille, ni anonymisés. Le décompte du quota d'offre. */
  patients: number;
  /** Rendez-vous du mois civil en cours (Europe/Luxembourg), annulés compris. */
  appointmentsThisMonth: number;
  /** Factures émises ce mois-ci, **en nombre**. Les brouillons ne comptent pas. */
  invoicesThisMonth: number;
  /** Tâches planifiées en échec sur vingt-quatre heures. Zéro est l'état normal. */
  failedJobs24h: number;
  /** Mois civil du cabinet auquel se rapportent les deux compteurs mensuels, en `YYYY-MM`. */
  month: string;
}

/* -------------------------------------------------------------------------- */
/* Cabinets                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Tris servis. Quatre, et quatre seulement : ce sont ceux qui ont un sens pour surveiller un parc
 * — qui est gros, qui est neuf, qui ne bouge plus.
 */
export const platformOrganizationSortFields = ['name', 'created', 'patients', 'activity'] as const;
export type PlatformOrganizationSortField = (typeof platformOrganizationSortFields)[number];

export const platformOrganizationListQuerySchema = z
  .object({
    /** Recherche sur le nom du cabinet. Rien d'autre n'est cherchable, et c'est voulu. */
    search: z.string().trim().max(120).optional(),
    sort: z.enum(platformOrganizationSortFields).default('name'),
    direction: z.enum(sortDirections).default('asc'),
    plan: z.enum(subscriptionPlans).optional(),
    /** `true` ne rend que les cabinets suspendus, `false` que les ouverts. Absent : les deux. */
    suspended: z.enum(['true', 'false']).optional(),
    /**
     * `true` : cabinets dont une suppression est planifiée (`scheduledDeletionAt` non nulle).
     * `false` : les autres. Absent : les deux.
     */
    scheduledDeletion: z.enum(['true', 'false']).optional(),
    ...paginationFields,
  })
  .strict();
export type PlatformOrganizationListQuery = z.infer<typeof platformOrganizationListQuerySchema>;

/** Une ligne de la liste des cabinets. Des compteurs et des dates, rien qui vienne d'un dossier. */
export interface PlatformOrganizationDto {
  id: string;
  name: string;
  plan: SubscriptionPlan;
  subscriptionStatus: SubscriptionStatus;
  /** Membres actifs, praticiens et personnel d'accueil confondus. */
  members: number;
  patients: number;
  /** Rendez-vous commençant dans les trente derniers jours. La mesure d'activité la plus directe. */
  appointments30d: number;
  /**
   * Dernière écriture du cabinet — consultation, rendez-vous ou dossier créé. Nulle pour un cabinet
   * qui n'a jamais rien fait, ce qui est exactement l'information qu'on cherche.
   */
  lastActivityAt: string | null;
  /** Intégrations activées : Doctena, Docalia, Buckaroo. */
  integrationsConnected: number;
  /** Travaux de synchronisation en échec que personne n'a repris. */
  syncErrorsOpen: number;
  /** Octets de documents vivants. Ni les corbeilles, ni les objets déjà purgés. */
  documentBytes: number;
  suspendedAt: string | null;
  suspensionReason: string | null;
  /** Fin de l'essai, quand le cabinet est entré dans le pipeline d'effacement. */
  trialEndedAt: string | null;
  /** Date d'effacement prévue. Nulle hors pipeline. */
  scheduledDeletionAt: string | null;
  /** Dernier courriel d'effacement parti. */
  deletionNoticeStage: DeletionNoticeStage;
  createdAt: string;
}

/** Résultat d'un report d'effacement : de quoi rafraîchir la ligne sans la relire. */
export interface OrganizationDeletionPostponeDto {
  organizationId: string;
  organizationName: string;
  scheduledDeletionAt: string;
  deletionNoticeStage: DeletionNoticeStage;
  deletionPostponedAt: string;
}

export type PlatformOrganizationListDto = PaginatedDto<PlatformOrganizationDto>;

/* -------------------------------------------------------------------------- */
/* Détail d'un cabinet                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Un membre du cabinet. **C'est le personnel, pas un patient** : ce sont les gens à qui l'on écrit
 * quand leur cabinet a un problème, et la console n'aurait aucune utilité sans eux.
 */
export interface PlatformMemberDto {
  userId: string;
  memberId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: MemberRole;
  isActive: boolean;
  lastLoginAt: string | null;
  joinedAt: string | null;
}

/** Santé d'un flux d'intégration, telle que le disjoncteur la voit. */
export interface PlatformIntegrationHealthDto {
  provider: string;
  stream: string;
  isEnabled: boolean;
  lastSuccessAt: string | null;
  lastErrorAt: string | null;
  /**
   * Message **technique** et tronqué, tel que la table le porte : jamais un nom, une adresse ni un
   * identifiant de patient.
   */
  lastError: string | null;
  consecutiveFailures: number;
  /** Tant que cet instant n'est pas passé, le flux n'est pas relancé. */
  pausedUntil: string | null;
}

/** Usage d'un quota face à ce que l'offre autorise. `limit` vaut `-1` quand il n'y a pas de limite. */
export interface PlatformQuotaUsageDto {
  limit: number;
  used: number;
}

export interface PlatformOrganizationDetailDto {
  organization: PlatformOrganizationDto;
  /**
   * L'offre est-elle commandée par un abonnement Stripe en cours ?
   *
   * Vrai quand l'abonnement est actif **ou en impayé** — un impayé ne ferme rien, l'abonnement
   * existe toujours et Stripe reste la source. La console s'en sert pour ne pas proposer un
   * changement d'offre à la main qu'elle refuserait au clic (`409 managed_by_stripe`) : mieux
   * vaut un bouton absent qu'un bouton qui échoue.
   */
  managedByStripe: boolean;
  /** L'offre **effective** : un abonnement annulé retombe sur la gratuite, quotas compris. */
  effectivePlan: SubscriptionPlan;
  features: Record<PlanFeature, boolean>;
  quotas: {
    patients: PlatformQuotaUsageDto;
    practitioners: PlatformQuotaUsageDto;
  };
  members: PlatformMemberDto[];
  integrations: PlatformIntegrationHealthDto[];
}
